/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import * as AlertDialog from "@radix-ui/react-dialog";
import { CloseIcon } from "@vector-im/compound-design-tokens/assets/web/icons";
import { localUsername } from "./localUsername";
import { localizedRequestError } from "../../utils/localizedError";
import { useTranslation } from "react-i18next";

import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  type FC,
  type FormEvent,
} from "react";
import { Button } from "@vector-im/compound-web";
import { FieldRow, InputField, ErrorMessage } from "../../input/Input";
import { useClient } from "../../ClientContext";
import { backendFetch, BackendError } from "../../utils/backend";
import styles from "./CreateUserModal.module.css";
import { OrganizerPasswordReset } from "./OrganizerPasswordReset";
import { generateOrganizerPassword } from "./password";

interface Organizer {
  userId: string;
  displayName?: string;
  canManage?: boolean;
  status: "active" | "disabled" | "provisioning" | "failed";
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateUserModal: FC<Props> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { client } = useClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [scope, setScope] = useState("mine");
  const [search, setSearch] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const scrollContent = useRef<HTMLDivElement>(null);
  const [retainedHeight, setRetainedHeight] = useState(0);
  const retainHeight = (): void => {
    setRetainedHeight(
      scrollContent.current?.getBoundingClientRect().height ?? 0,
    );
  };
  const [organizers, setOrganizers] = useState<Organizer[]>([]);
  const [revision, setRevision] = useState(0);
  const organizerNodes = useRef(new Map<string, HTMLDivElement>());
  const previousPositions = useRef<Map<string, number> | null>(null);
  const reorderAnimations = useRef<Animation[]>([]);
  const toastSequence = useRef(0);
  useLayoutEffect(() => {
    const before = previousPositions.current;
    previousPositions.current = null;
    if (
      !before ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const scroll = scrollContent.current?.scrollTop ?? 0;
    for (const [id, node] of organizerNodes.current) {
      const oldTop = before.get(id);
      const delta =
        oldTop === undefined
          ? 0
          : oldTop - (node.getBoundingClientRect().top + scroll);
      if (Math.abs(delta) < 1 || typeof node.animate !== "function") continue;
      const animation = node.animate(
        [
          { transform: `translateY(${delta}px)` },
          { transform: "translateY(0)" },
        ],
        { duration: 420, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
      );
      reorderAnimations.current.push(animation);
    }
  }, [organizers]);
  useEffect(
    () => () => {
      reorderAnimations.current.forEach((animation) => animation.cancel());
      reorderAnimations.current = [];
      previousPositions.current = null;
    },
    [isOpen],
  );
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState<false | "password" | "credentials">(
    false,
  );
  const [copyVersion, setCopyVersion] = useState(0);
  const [confirmation, setConfirmation] = useState<
    (Organizer & { hide?: boolean }) | null
  >(null);
  const [accessNotice, setAccessNotice] = useState<{
    id: number;
    text: string;
  } | null>(null);
  const [created, setCreated] = useState<{
    userId: string;
    password: string;
  } | null>(null);
  useEffect(() => {
    if (!accessNotice) return;
    const timer = window.setTimeout(() => setAccessNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [accessNotice]);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 5000);
    return () => window.clearTimeout(timer);
  }, [copied, copyVersion]);
  useEffect(() => {
    setPassword(isOpen ? generateOrganizerPassword() : "");
    setShowPassword(false);
    setCopied(false);
    setCreated(null);
    setError(null);
    setSuccessMsg(null);
    setConfirmation(null);
    setAccessNotice(null);
    setRetainedHeight(0);
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) {
      setOrganizers([]);
      return;
    }
    let cancelled = false;
    setListLoading(true);
    backendFetch(
      client,
      "/api/admin/organizers" + (scope === "mine" ? "" : `?scope=${scope}`),
    )
      .then(async (res) => await res.json())
      .then((data) => {
        if (!cancelled) setOrganizers(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setOrganizers([]);
          setError(localizedRequestError(err, t));
        }
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, isOpen, revision, t, scope]);

  // Объект с переводами
  const uiText = {
    title: t("instance.create_user_modal.title"),
    loginLabel: t("instance.create_user_modal.loginLabel"),
    passLabel: t("instance.create_user_modal.passLabel"),
    btnSubmit: t("instance.create_user_modal.btnSubmit"),
    btnLoading: t("instance.create_user_modal.btnLoading"),
    errNoToken: t("instance.create_user_modal.errNoToken"),
    errDefault: t("instance.create_user_modal.errDefault"),
    errServer: t("instance.create_user_modal.errServer"),
    errNoEndpoint: t("instance.create_user_modal.errNoEndpoint"),
    errNotJson: t("instance.create_user_modal.errNotJson"),
    successAlert: (user: string) =>
      t("instance.create_user_modal.user_value_successfully_created", {
        user: user,
      }),
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const accessToken = client?.getAccessToken();

      if (!accessToken) {
        throw new Error(uiText.errNoToken);
      }

      const baseUrl = import.meta.env.VITE_BACKEND_URL || "";
      const endpoint = `${baseUrl}/api/admin/create-user`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ username, password }),
      });

      const contentType = response.headers.get("content-type");
      const isJson = contentType && contentType.includes("application/json");

      if (!response.ok) {
        if (isJson) {
          const errorData = await response.json();
          throw new BackendError(
            errorData.error || uiText.errDefault,
            response.status,
          );
        } else {
          throw new Error(
            `${uiText.errServer} (code ${response.status}): ${uiText.errNoEndpoint}`,
          );
        }
      }

      if (!isJson) {
        throw new Error(uiText.errNotJson);
      }

      const data = (await response.json()) as { userId: string };
      setCreated({ userId: data.userId, password });
      setCopied(false);
      setSuccessMsg(uiText.successAlert(username));
      setUsername("");
      setPassword(generateOrganizerPassword());
      setRevision((value) => value + 1);
    } catch (err: any) {
      setError(localizedRequestError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const copyCredentials = async (
    credentials: string,
    target: "password" | "credentials" = "password",
  ) => {
    try {
      await navigator.clipboard.writeText(credentials);
      setCopied(target);
      setCopyVersion((value) => value + 1);
    } catch {
      setError(
        t(
          "instance.create_user_modal.could_not_copy_select_and_copy_the_credentials_manually",
        ),
      );
    }
  };

  const toggleOrganizer = async (organizer: Organizer & { hide?: boolean }) => {
    setLoading(true);
    setError(null);
    setSuccessMsg(null);
    try {
      await backendFetch(
        client,
        organizer.hide
          ? "/api/admin/organizers/hide"
          : "/api/admin/organizers/status",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: organizer.userId,
            ...(!organizer.hide && {
              enabled: organizer.status === "disabled",
            }),
          }),
        },
      );
      setAccessNotice({
        id: ++toastSequence.current,
        text: t(
          organizer.hide
            ? "instance.create_user_modal.account_hidden"
            : organizer.status === "disabled"
              ? "instance.create_user_modal.access_enabled"
              : "instance.create_user_modal.access_disabled",
          { user: localUsername(organizer.userId) },
        ),
      });
      retainHeight();
      const scroll = scrollContent.current?.scrollTop ?? 0;
      previousPositions.current = new Map(
        [...organizerNodes.current].map(([id, node]) => [
          id,
          node.getBoundingClientRect().top + scroll,
        ]),
      );
      reorderAnimations.current.forEach((animation) => animation.cancel());
      reorderAnimations.current = [];
      setOrganizers((value) =>
        organizer.hide
          ? value.filter((person) => person.userId !== organizer.userId)
          : value.map((person) =>
              person.userId === organizer.userId
                ? {
                    ...person,
                    status:
                      organizer.status === "disabled" ? "active" : "disabled",
                  }
                : person,
            ),
      );
      setConfirmation(null);
    } catch (err) {
      setError(localizedRequestError(err, t));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AlertDialog.Root
        open={isOpen}
        onOpenChange={(open) => {
          if (!open && !loading) onClose();
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay style={{ ...overlayStyle, display: "block" }} />
          <AlertDialog.Content
            className={styles.managementFrame}
            style={{
              ...overlayStyle,
              backgroundColor: "transparent",
              backdropFilter: "none",
              WebkitBackdropFilter: "none",
              pointerEvents: "none",
            }}
            aria-describedby={undefined}
            aria-labelledby="organizer-dialog-title"
            onInteractOutside={(event) => event.preventDefault()}
            onEscapeKeyDown={(event) => {
              if (loading) event.preventDefault();
            }}
          >
            {accessNotice && (
              <div
                key={accessNotice.id}
                className={styles.accessToast}
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                <span>{accessNotice.text}</span>
                <button
                  type="button"
                  className={styles.toastClose}
                  aria-label={t("action.close")}
                  onClick={() => setAccessNotice(null)}
                >
                  <CloseIcon aria-hidden width={18} height={18} />
                </button>
              </div>
            )}
            <div className={styles.modal} style={modalStyle}>
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                disabled={loading}
                aria-label={t("action.close")}
              >
                <CloseIcon aria-hidden width={20} height={20} />
              </button>
              <div
                ref={scrollContent}
                className={styles.scrollContent}
                style={{
                  minHeight: retainedHeight
                    ? `min(${retainedHeight}px, calc(100dvh - 32px))`
                    : undefined,
                }}
              >
                <AlertDialog.Title asChild>
                  <h3
                    id="organizer-dialog-title"
                    style={{
                      marginTop: 0,
                      marginBottom: "16px",
                      paddingInlineEnd: "32px",
                    }}
                  >
                    {uiText.title}
                  </h3>
                </AlertDialog.Title>
                <form onSubmit={(event) => void handleSubmit(event)}>
                  <FieldRow>
                    <InputField
                      id="organizer-username"
                      type="text"
                      label={uiText.loginLabel}
                      placeholder="ivan.ivanov"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </FieldRow>
                  <FieldRow className={styles.passwordField}>
                    <InputField
                      id="organizer-password"
                      type={showPassword ? "text" : "password"}
                      label={uiText.passLabel}
                      placeholder="••••••••"
                      minLength={10}
                      maxLength={256}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </FieldRow>
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      flexWrap: "wrap",
                      marginTop: "8px",
                      marginBottom: "24px",
                    }}
                  >
                    <Button
                      type="button"
                      kind="secondary"
                      size="md"
                      style={passwordActionStyle}
                      disabled={loading}
                      onClick={() => {
                        setPassword(generateOrganizerPassword());
                        setCopied(false);
                      }}
                    >
                      {t("instance.create_user_modal.generate_password")}
                    </Button>
                    <Button
                      type="button"
                      kind="secondary"
                      size="md"
                      style={passwordActionStyle}
                      onClick={() => setShowPassword((value) => !value)}
                    >
                      {showPassword
                        ? t("instance.create_user_modal.hide")
                        : t("instance.create_user_modal.show")}
                    </Button>
                    <Button
                      type="button"
                      kind="secondary"
                      size="md"
                      style={passwordActionStyle}
                      onClick={() => void copyCredentials(password)}
                    >
                      <span
                        className={styles.copyText}
                        data-copied={copied === "password"}
                      >
                        <span aria-hidden={copied === "password"}>
                          {t("instance.create_user_modal.copy_password")}
                        </span>
                        <span aria-hidden={copied !== "password"}>
                          {t("instance.create_user_modal.copied")}
                        </span>
                      </span>
                    </Button>
                  </div>

                  {error && <ErrorMessage error={new Error(error)} />}
                  {successMsg && (
                    <p
                      role="status"
                      style={{
                        color: "var(--app-accent)",
                        fontSize: "0.9rem",
                        margin: "20px 0 12px",
                      }}
                    >
                      {successMsg}
                    </p>
                  )}
                  {created && (
                    <section
                      aria-label={t(
                        "instance.create_user_modal.new_organizer_credentials",
                      )}
                      style={{
                        display: "grid",
                        gap: "12px",
                        marginTop: "16px",
                        padding: "16px",
                        border: "1px solid var(--app-border)",
                        borderRadius: "12px",
                        background: "var(--app-field)",
                      }}
                    >
                      <p style={{ margin: 0, overflowWrap: "anywhere" }}>
                        {localUsername(created.userId)}
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 13,
                          color: "var(--app-muted)",
                        }}
                      >
                        {t(
                          "instance.create_user_modal.copy_and_send_these_credentials_to_the_organizer_the_password_is_",
                        )}
                      </p>
                      <InputField
                        id="organizer-issued-password"
                        type="text"
                        label={t("instance.create_user_modal.issued_password")}
                        value={created.password}
                        readOnly
                      />
                      <Button
                        type="button"
                        kind="secondary"
                        style={{ ...passwordActionStyle, justifySelf: "start" }}
                        onClick={() =>
                          void copyCredentials(
                            `${localUsername(created.userId)}\n${created.password}`,
                            "credentials",
                          )
                        }
                      >
                        <span
                          className={styles.copyText}
                          data-copied={copied === "credentials"}
                        >
                          <span aria-hidden={copied === "credentials"}>
                            {t(
                              "instance.create_user_modal.copy_login_and_password",
                            )}
                          </span>
                          <span aria-hidden={copied !== "credentials"}>
                            {t("instance.create_user_modal.copied")}
                          </span>
                        </span>
                      </Button>
                    </section>
                  )}

                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      marginTop: "20px",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      type="submit"
                      className={`glass-button ${styles.create}`}
                      disabled={loading}
                    >
                      {loading ? uiText.btnLoading : uiText.btnSubmit}
                    </Button>
                  </div>
                </form>
                {
                  <section
                    aria-label={t(
                      "instance.create_user_modal.organizers_title",
                    )}
                    style={{
                      marginTop: "28px",
                      paddingTop: "24px",
                      borderTop: "1px solid var(--app-border)",
                    }}
                  >
                    <h4
                      style={{ margin: "0 0 16px", color: "var(--app-muted)" }}
                    >
                      {t("instance.create_user_modal.organizers_title")}
                    </h4>
                    <div className={styles.organizerFilters}>
                      <select
                        aria-label={t(
                          "instance.create_user_modal.organizer_scope",
                        )}
                        value={scope}
                        onChange={(event) => {
                          retainHeight();
                          setListLoading(true);
                          setScope(event.target.value);
                        }}
                      >
                        <option value="mine">
                          {t("instance.create_user_modal.scope_mine")}
                        </option>
                        <option value="other">
                          {t("instance.create_user_modal.scope_other")}
                        </option>
                        <option value="all">
                          {t("instance.create_user_modal.scope_all")}
                        </option>
                      </select>
                      <input
                        type="search"
                        aria-label={t(
                          "instance.create_user_modal.search_organizers",
                        )}
                        placeholder={t(
                          "instance.create_user_modal.search_organizers",
                        )}
                        value={search}
                        onChange={(event) => {
                          retainHeight();
                          setSearch(event.target.value);
                        }}
                      />
                    </div>
                    <div
                      className={styles.organizerResults}
                      aria-busy={listLoading}
                    >
                      {listLoading && (
                        <p role="status" className={styles.refreshNotice}>
                          {t("common.loading")}
                        </p>
                      )}
                      <fieldset
                        disabled={listLoading}
                        className={styles.organizerRows}
                        data-updating={listLoading || undefined}
                      >
                        {!listLoading &&
                          !organizers.some((person) =>
                            `${person.displayName ?? ""} ${localUsername(person.userId)}`
                              .toLocaleLowerCase()
                              .includes(search.trim().toLocaleLowerCase()),
                          ) && (
                            <p>
                              {t("instance.create_user_modal.no_organizers")}
                            </p>
                          )}
                        {[...organizers]
                          .filter((person) =>
                            `${person.displayName ?? ""} ${localUsername(person.userId)}`
                              .toLocaleLowerCase()
                              .includes(search.trim().toLocaleLowerCase()),
                          )
                          .sort(
                            (a, b) =>
                              Number(a.status === "disabled") -
                              Number(b.status === "disabled"),
                          )
                          .map((organizer) => (
                            <div
                              key={organizer.userId}
                              ref={(node) => {
                                if (node)
                                  organizerNodes.current.set(
                                    organizer.userId,
                                    node,
                                  );
                                else
                                  organizerNodes.current.delete(
                                    organizer.userId,
                                  );
                              }}
                              role="group"
                              aria-label={localUsername(organizer.userId)}
                              data-disabled={
                                organizer.status === "disabled" || undefined
                              }
                              className={styles.organizer}
                              style={{
                                marginBottom: "12px",
                                padding: "16px",
                                border: "1px solid var(--app-border)",
                                borderRadius: "12px",
                                background: "var(--app-field)",
                                overflowWrap: "anywhere",
                              }}
                            >
                              <p
                                style={{
                                  margin: "0 0 12px",
                                  fontSize: 14,
                                  fontWeight: 600,
                                }}
                              >
                                {organizer.status === "disabled" &&
                                  organizer.canManage !== false && (
                                    <button
                                      type="button"
                                      className={styles.hideAccount}
                                      disabled={loading}
                                      aria-label={t(
                                        "instance.create_user_modal.hide_account",
                                        {
                                          user: localUsername(organizer.userId),
                                        },
                                      )}
                                      onClick={() => {
                                        setError(null);
                                        setConfirmation({
                                          ...organizer,
                                          hide: true,
                                        });
                                      }}
                                    >
                                      <CloseIcon
                                        aria-hidden
                                        width={18}
                                        height={18}
                                      />
                                    </button>
                                  )}
                                {organizer.displayName &&
                                  organizer.displayName !==
                                    localUsername(organizer.userId) && (
                                    <span className={styles.organizerName}>
                                      {organizer.displayName}
                                    </span>
                                  )}
                                {localUsername(organizer.userId)}
                                {organizer.status === "disabled" && (
                                  <span className={styles.disabledLabel}>
                                    {t(
                                      "instance.create_user_modal.disabled_status",
                                    )}
                                  </span>
                                )}
                              </p>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  alignItems: "flex-start",
                                }}
                              >
                                {organizer.canManage === false ? (
                                  <span className={styles.viewOnly}>
                                    {t("instance.create_user_modal.view_only")}
                                  </span>
                                ) : organizer.status === "active" ||
                                  organizer.status === "disabled" ? (
                                  <Button
                                    type="button"
                                    kind="secondary"
                                    style={passwordActionStyle}
                                    disabled={loading}
                                    onClick={() => {
                                      setError(null);
                                      setConfirmation(organizer);
                                    }}
                                  >
                                    {organizer.status === "active"
                                      ? t(
                                          "instance.create_user_modal.disable_access",
                                        )
                                      : t(
                                          "instance.create_user_modal.enable_access",
                                        )}
                                  </Button>
                                ) : (
                                  <p>
                                    {t(
                                      "instance.create_user_modal.account_provisioning_needs_review",
                                    )}
                                  </p>
                                )}
                                {client &&
                                  organizer.canManage !== false &&
                                  ["active", "disabled"].includes(
                                    organizer.status,
                                  ) && (
                                    <OrganizerPasswordReset
                                      client={client}
                                      userId={organizer.userId}
                                    />
                                  )}
                              </div>
                            </div>
                          ))}
                      </fieldset>
                    </div>
                  </section>
                }
              </div>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
      <AlertDialog.Root
        open={!!confirmation}
        onOpenChange={(open) => {
          if (!open && !loading) setConfirmation(null);
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className={styles.confirmationOverlay} />
          <AlertDialog.Content
            className={styles.confirmation}
            role="alertdialog"
            onEscapeKeyDown={(event) => {
              if (loading) event.preventDefault();
            }}
          >
            <AlertDialog.Title className={styles.confirmationTitle}>
              {t(
                confirmation?.hide
                  ? "instance.create_user_modal.hide_title"
                  : confirmation?.status === "disabled"
                    ? "instance.create_user_modal.enable_access"
                    : "instance.create_user_modal.disable_access",
              )}
            </AlertDialog.Title>
            <AlertDialog.Description>
              {confirmation &&
                t(
                  confirmation.hide
                    ? "instance.create_user_modal.confirm_hide"
                    : confirmation.status === "disabled"
                      ? "instance.create_user_modal.confirm_enable"
                      : "instance.create_user_modal.confirm_disable",
                  { user: localUsername(confirmation.userId) },
                )}
            </AlertDialog.Description>
            {error && <p role="alert">{error}</p>}
            <div>
              <AlertDialog.Close asChild>
                <Button
                  type="button"
                  kind="secondary"
                  disabled={loading}
                  style={passwordActionStyle}
                >
                  {t("instance.create_user_modal.cancel_action")}
                </Button>
              </AlertDialog.Close>
              <Button
                type="button"
                disabled={loading}
                style={passwordActionStyle}
                onClick={() => {
                  if (confirmation) void toggleOrganizer(confirmation);
                }}
              >
                {t("instance.create_user_modal.confirm_action")}
              </Button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
};

const passwordActionStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 500,
  lineHeight: "20px",
  padding: "6px 10px",
  minBlockSize: "36px",
};

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "var(--app-scrim)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  padding: "16px",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: "var(--app-modal)",
  backdropFilter: "blur(28px) saturate(135%)",
  WebkitBackdropFilter: "blur(28px) saturate(135%)",
  border: "1px solid var(--glass-edge)",
  boxShadow:
    "inset 0 1px 0 var(--glass-highlight), 0 24px 80px var(--glass-shadow)",
  boxSizing: "border-box",
  padding: "24px",
  borderRadius: "28px",
  width: "100%",
  maxWidth: "448px",
  color: "var(--app-text)",
  maxHeight: "calc(100dvh - 32px)",
  overflowY: "auto",
};
